import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { message, Modal, Input } from 'antd';
import Button from '../../../../shared/globalStyling/Button';
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  LinkOutlined,
  CloseOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useBreakpoints } from '../../../../shared/globalStyling/screenSizes';
import { CSVLink } from 'react-csv';

// CONFIGS
import { OrganizationConfig } from '../../configs/organizationConfig';

// COMPONENTS
import OrganizationCsvLocationUpload from '../OrganizationCsvLocationUpload';
import TemplateCSVDownloader from '../../../../shared/TemplateCSVDownloader';
import TargetingLocationListView from './TargetingLocationListView';
import TargetingLocationForm from './TargetingLocationForm';
import CreateAudienceForm from '../CreateAudienceForm';
import TargetingLocationPreviewImport from './TargetingLocationPreviewImport';
import TargetingAddToAudience from './TargetingAddToAudience';
import TargetingAddressMap from './components/TargetingAddressMap';

const flattenLocationForCsv = (location) => ({
  id: location.id,
  name: location.name,
  description: location.description,
  street: location.street,
  city: location.city,
  state: location.state,
  province: location.province,
  lat: location.lat,
  lng: location.lng,
  formattedAddress: location.formattedAddress,
  email: location.email,
  locationKey: location.locationKey,
  phone: location.phone,
  contact: location.contact,
  url: location.url,
  zip: location.zip,
  country: location.country,
  county: location.county,
  catalogs: location.catalogs ? location.catalogs.map((c) => c.id).join(', ') : '',
  hours: location.hours,
  createdAt: location.createdAt,
  updatedAt: location.updatedAt,
  org: location.org ? location.org.name : '',
  geoframe: location.geoframe ? location.geoframe.name : '',
  transConfig: location.transConfig
    ? [location.transConfig.cart, location.transConfig.menuDataKey].filter(Boolean).join(', ')
    : '',
  licenses: location.licenses
    ? location.licenses.map((l) => l.licenseIdentifier).filter(Boolean).join(', ')
    : '',
  audienceType: location.audienceType,
  createdBy: location.createdBy ? location.createdBy.name : '',
  updatedBy: location.updatedBy ? location.updatedBy.name : '',
});

export default function TargetingLocations({
  toolbarContainer,
  attributionAudienceName,
  createTargetingLocation,
  deleteLocation,
  google,
  locationList,
  onChangeStateValue,
  onCsvUploadComplete,
  onSubmitAudience,
  onAddToExistingAudience,
  selectedLocations,
  updateTargetingLocation,
  isTargetingPreviewModalOpen,
  targetingLocationsPreviewData,
  closeTargetingPreviewModal,
  bulkImportTargetingLocations,
  selectTargetingLocationPreview,
  deleteBulkLocation,
  targetingAudiences,
  selectedOrg,
}) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteBulk, setIsDeleteBulk] = useState(false);
  const [isAudienceFormOpen, setIsAudienceFormOpen] = useState(false);
  const [isAddToAudienceGroupOpen, setIsAddToAudienceGroupOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [highlightedLocationId, setHighlightedLocationId] = useState(null);
  const [searchText, setSearchText] = useState('');

  const { isTablet } = useBreakpoints();

  const openCreateForm = () => {
    setSelectedLocation(null);
    toggleForm(true);
  };

  useEffect(() => {
    if (locationList && selectedOrg) {
      const locations = locationList.filter(
        (location) => location && location.org && location.org.id === selectedOrg.id
      );
      setFilteredLocations(locations);
    }
  }, [locationList, selectedOrg]);

  const searchedLocations = searchText
    ? filteredLocations.filter((loc) =>
        (loc.name || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (loc.formattedAddress || '').toLowerCase().includes(searchText.toLowerCase())
      )
    : filteredLocations;

  const toggleForm = (val) => {
    setIsFormOpen(val);
  };

  const onCloseForm = () => {
    setSelectedLocation(null);
    toggleForm(false);
  };

  const onDeleteTargetingLocation = (Id) => {
    toggleForm(false);

    message.loading('Action in progress..', 0);

    deleteLocation(Id)
      .then(() => {
        message.destroy();
        message.success(`Successfully deleted retail Location`);
      })
      .catch((err) => {
        message.destroy();
        message.error(`Error: Cannot delete retail Location`);
      });
  };

  const onEditLocation = (location) => {
    setSelectedLocation(location);
    toggleForm(true);
  };

  const onSubmitForm = async (location) => {
    try {
      let success = false;
      if (selectedLocation) {
        success = await Promise.race([
          updateTargetingLocation({
            ...selectedLocation,
            ...location,
          }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Update timeout after 30 seconds')), 30000)
          ),
        ]);
      } else {
        success = await createTargetingLocation(location);
      }

      if (success !== false) {
        setSelectedLocation(null);
        toggleForm(false);
      }
    } catch (error) {
      console.error('Error in onSubmitForm:', error);
    }
  };

  const onCloseAudienceForm = () => {
    setIsAudienceFormOpen(false);
  };

  const onSubmitAudienceForm = () => {
    onCloseAudienceForm();
    onSubmitAudience('TARGETING');
  };

  const handleCancelDeleteBulk = () => {
    setIsDeleteBulk(false);
  };

  const handleOkDeleteBulk = () => {
    deleteBulkLocation();
    setIsDeleteBulk(false);
  };

  const handleCancelAddToAudienceGroup = () => {
    setIsAddToAudienceGroupOpen(false);
  };

  const handleSubmitAddToAudience = async (audienceId) => {
    try {
      await onAddToExistingAudience(audienceId);
      setIsAddToAudienceGroupOpen(false);
    } catch (error) {
      console.error('Error adding locations to audience:', error);
    }
  };

  const handlePinClick = (location) => {
    setHighlightedLocationId(location.id);
  };

  const handleClearSelection = () => {
    onChangeStateValue('selectedLocations', []);
  };

  const handleBulkImport = async (data) => {
    await bulkImportTargetingLocations(data);
    setIsFormOpen(false);
  };

  const targetingOnlyLocations = searchedLocations.filter(
    (loc) => loc.audienceType === 'TARGETING'
  );

  const hasSelection = selectedLocations && selectedLocations.length > 0;

  // ─── Tab bar toolbar: search + primary action (desktop only) ────────
  const toolbarButtons = (
    <>
      <div className="targeting-search">
        <Input
          placeholder="Search locations"
          value={searchText}
          allowClear
          prefix={<SearchOutlined />}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>
      {!isTablet && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateForm}
        >
          Add Location
        </Button>
      )}
    </>
  );

  return (
    <div className="targeting-address">
      {/* Portal search + actions into the tab bar */}
      {toolbarContainer && ReactDOM.createPortal(toolbarButtons, toolbarContainer)}

      {/* Modals */}
      {isFormOpen && (
        <TargetingLocationForm
          selectedLocation={selectedLocation}
          isFormOpen={isFormOpen}
          google={google}
          targetingLocations={locationList}
          closeForm={onCloseForm}
          submitForm={onSubmitForm}
          onCsvUploadComplete={onCsvUploadComplete}
          csvUploadComponent={<OrganizationCsvLocationUpload onComplete={onCsvUploadComplete} />}
          csvTemplateComponent={
            <TemplateCSVDownloader
              rows={OrganizationConfig.csvTemplate.rows}
              templateName={OrganizationConfig.csvTemplate.templateName}
              title="Download Template"
              tooltipMessage="Download CSV template for bulk location upload"
              type="default"
              styles={{ minWidth: 'auto' }}
            />
          }
        />
      )}

      {isAudienceFormOpen && (
        <CreateAudienceForm
          attributionAudienceName={attributionAudienceName}
          isFormOpen={isAudienceFormOpen}
          closeForm={onCloseAudienceForm}
          onChangeStateValue={onChangeStateValue}
          submitForm={onSubmitAudienceForm}
        />
      )}

      {isDeleteBulk && hasSelection && (
        <Modal
          title="Confirm Action"
          visible={isDeleteBulk}
          onOk={handleOkDeleteBulk}
          onCancel={handleCancelDeleteBulk}
          wrapClassName="targeting-root"
        >
          <p>
            Are you sure you want to delete{' '}
            {`${selectedLocations.length} ${
              selectedLocations.length === 1 ? 'location' : 'locations'
            }`}
            ?
          </p>
        </Modal>
      )}

      <TargetingAddToAudience
        isOpen={isAddToAudienceGroupOpen && hasSelection}
        onCancel={handleCancelAddToAudienceGroup}
        onSubmit={handleSubmitAddToAudience}
        selectedLocations={selectedLocations}
        targetingAudiences={targetingAudiences}
      />

      {isTargetingPreviewModalOpen && (
        <TargetingLocationPreviewImport
          isOpen={isTargetingPreviewModalOpen}
          targetingLocationsPreviewData={targetingLocationsPreviewData}
          closeTargetingPreviewModal={closeTargetingPreviewModal}
          bulkImportTargetingLocations={handleBulkImport}
          selectTargetingLocationPreview={selectTargetingLocationPreview}
        />
      )}

      {/* ─── Selection alert bar (appears when rows are selected) ────────── */}
      {hasSelection && (
        <div className="targeting-selection-alert" role="status" aria-live="polite">
          <div className="targeting-selection-alert__info">
            <span className="targeting-selection-alert__count">{selectedLocations.length}</span>
            <span className="targeting-selection-alert__text">
              location{selectedLocations.length !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="targeting-selection-alert__actions">
            <button
              type="button"
              className="targeting-selection-alert__link"
              onClick={() => setIsAudienceFormOpen(true)}
            >
              <PlusOutlined className="targeting-selection-alert__link-icon" />
              Create Group
            </button>
            <span className="targeting-selection-alert__separator">&middot;</span>
            <button
              type="button"
              className="targeting-selection-alert__link"
              onClick={() => setIsAddToAudienceGroupOpen(true)}
            >
              <LinkOutlined className="targeting-selection-alert__link-icon" />
              Add to Audience
            </button>
            <span className="targeting-selection-alert__separator">&middot;</span>
            <CSVLink
              filename={`TargetingLocations.csv`}
              data={selectedLocations.map(flattenLocationForCsv)}
              className="targeting-selection-alert__link"
              style={{ textDecoration: 'none' }}
            >
              <DownloadOutlined className="targeting-selection-alert__link-icon" />
              Download CSV
            </CSVLink>
            <span className="targeting-selection-alert__separator">&middot;</span>
            <button
              type="button"
              className="targeting-selection-alert__link targeting-selection-alert__link--danger"
              onClick={() => setIsDeleteBulk(true)}
            >
              <DeleteOutlined className="targeting-selection-alert__link-icon" />
              Delete
            </button>
            <button
              type="button"
              className="targeting-selection-alert__close"
              onClick={handleClearSelection}
              aria-label="Clear selection"
            >
              <CloseOutlined />
            </button>
          </div>
        </div>
      )}

      {/* Split content: table + map (map hidden on mobile via CSS) */}
      <div className="targeting-address__content">
        <div className="targeting-address__table">
          <TargetingLocationListView
            deleteLocation={onDeleteTargetingLocation}
            editLocation={onEditLocation}
            onChangeStateValue={onChangeStateValue}
            targetingLocationList={searchedLocations}
            targetingAudiences={targetingAudiences}
            selectedLocations={selectedLocations}
            highlightedLocationId={highlightedLocationId}
          />
        </div>

        <div className="targeting-address__map">
          <TargetingAddressMap
            locations={targetingOnlyLocations}
            google={google}
            onPinClick={handlePinClick}
            highlightedId={highlightedLocationId}
          />
        </div>
      </div>

      {/* Mobile FAB */}
      {isTablet && (
        <button
          type="button"
          className="targeting-fab"
          onClick={openCreateForm}
          aria-label="Add Location"
        >
          <PlusOutlined />
        </button>
      )}
    </div>
  );
}
